import { FlightPathXEyeTrackingCanvas as FlightPathXEyeTrackingCanvasComponent } from "heat-next";
import { Vector3 } from "three";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

interface FlightPathXEyeTrackingCanvasProps {
    isInsideModal?: boolean;
    subDimensionsData?: any;
}

const FlightPathXEyeTrackingCanvas = ({
    isInsideModal,
    subDimensionsData,
}: FlightPathXEyeTrackingCanvasProps) => {
    const {
        gazeData,
        isFetchingGazeData,
        isErrorGazeData,
        flightCircuitData,
        isFetchingFlightCircuit,
        isErrorFlightCircuit,
    } = useSessionDetailsContext();

    //EyeTrackingCanvas
    const eyeTrackingCanvas = isInsideModal
        ? subDimensionsData?.flat()?.filter((item: any) => item?.chartType === "EyeTracking")
        : gazeData;

    //FlightPath
    const flightCircuit = isInsideModal
        ? subDimensionsData?.flat().filter((item: any) => item?.chartType === "FlightPath")
        : flightCircuitData;

    //Conversion function to turn the values array from gazeData
    // into the accepted format for the Eye Tracking component
    function convertToLineSegments(data: any) {
        const lines = [];

        for (let i = 0; i < data?.length - 1; i++) {
            const curr = data[i];
            const next = data[i + 1];

            const from = new Vector3(curr.x, curr.y, curr.z);
            const to = new Vector3(next.x, next.y, next.z);

            lines.push({
                from: { x: from.x, y: from.y },
                to: { x: to.x, y: to.y },
                captureDate: new Date(curr.timestamp).toISOString(),
            });
        }

        return lines;
    }

    const getEyeTrackingData = (circuit: any) => {
        const eyeTrackingDataPerCircuit = eyeTrackingCanvas?.filter((entry: any) => {
            return entry?.title === circuit?.title;
        });

        const eyeTracking = {
            data: {
                canvas: {
                    width: 496,
                    height: 288,
                    canvasFillColor: "#383838",
                },
                images: [
                    {
                        imageSrc: "aviation/cockpit",
                        centerX: 70,
                        centerY: 16,
                        width: 355.1436,
                        height: 254.1132,
                        opacity: 1,
                    },
                ],
                linesLayer: {
                    lines: convertToLineSegments(eyeTrackingDataPerCircuit?.[0]?.values), //Transform gaze_data for EyeTracking Canvas
                    defaultColor: "#c56e38",
                    defaultThickness: 3,
                    hasGradient: true,
                },
                shapeProps: {
                    shapeName: "Circle",
                    threshold: 5,
                    baseRadius: 10,
                    maxRadius: 10,
                    growthFactor: 10,
                    color: "#c56e38",
                    hasGradient: true,
                },
            },
        };

        return eyeTracking;
    };

    //Prepare data for FlightPathXEyeTrackingCanvas component per circuit
    const flightPathXEyeTrackingCanvasData = flightCircuit?.map((circuit: any) => {
        return {
            title: circuit.title || "Flight Path",
            eyeTrackingCanvasData: getEyeTrackingData(circuit),
            flightPathData: circuit,
        };
    });

    const isEyeTrackingCanvasEmptyState =
        !isErrorGazeData &&
        (!eyeTrackingCanvas ||
            eyeTrackingCanvas?.every((entry: any) => {
                return entry?.values?.length === 0;
            }));

    const isFlightCircuitDataEmptyState =
        !isErrorFlightCircuit &&
        (!flightPathXEyeTrackingCanvasData ||
            flightPathXEyeTrackingCanvasData?.pathData?.length === 0);

    const isEmptyState = () => {
        if (isInsideModal) {
            return subDimensionsData?.every((v: any) => v === undefined);
        } else if (isFlightCircuitDataEmptyState || isEyeTrackingCanvasEmptyState) {
            return true;
        }
    };

    if (isFetchingFlightCircuit || isFetchingGazeData) {
        return <ContentLoader height="28.875rem" />;
    }

    if (
        (!isInsideModal && isErrorFlightCircuit && !isFetchingFlightCircuit) ||
        (!isInsideModal && isErrorGazeData && !isFetchingGazeData)
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="25.875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-detail:no-flight-path-data"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="25.875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isFetchingFlightCircuit || isFetchingGazeData) {
        return <ContentLoader height={"26.4375rem"} />;
    }

    return (
        <FlightPathXEyeTrackingCanvasComponent
            dualPlaybackProps={{
                rowGap: "0.5rem",
            }}
            isInsideModal={isInsideModal}
            data={flightPathXEyeTrackingCanvasData}
        />
    );
};

export default FlightPathXEyeTrackingCanvas;
